import { useQuery } from "@tanstack/react-query";

import { getRanaApiV1 } from "@nens/rana/api-v1";

import { ProjectFile } from "@/types/api-model-simple-names";

import { useFileDownloadQuery } from "./query-file-download";
import { combineQueries } from "./utils/combine-queries";

// UTIL

// Counts non-printable characters (excluding common whitespace like tab, newline, and carriage return so \t, \n, \r).
// If more than a given ratio (default 30%) of the characters are non-printable, it assumes the content is binary.
const isProbablyBinary = (text: string, threshold = 0.3) => {
  let nonPrintable = 0;
  for (let i = 0; i < text.length; i++) {
    const charCode = text.charCodeAt(i);
    if (
      (charCode < 32 && charCode !== 9 && charCode !== 10 && charCode !== 13) || // exclude \t, \n, \r
      charCode === 65533 // replacement character used for decoding errors
    ) {
      nonPrintable++;
    }
  }
  const ratio = nonPrintable / text.length;
  return ratio > threshold;
};

// Single

const getFileTextQuery = (url: string | undefined | null) => {
  return getRanaApiV1().getQueryOptions<string>({
    queryKey: ["textFile", url],
    queryFn: async () => {
      if (!url) throw new Error("URL is undefined");

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch text file");
      }

      const text = await response.text();
      if (isProbablyBinary(text)) {
        throw new Error("Text is binary");
      }

      return text;
    },
    enabled: !!url,
  });
};

export const useFileTextQuery = (file: ProjectFile | undefined) => {
  const downloadQuery = useFileDownloadQuery(file);
  const fileTextQuery = useQuery(getFileTextQuery(downloadQuery.data));

  return combineQueries(fileTextQuery, downloadQuery);
};
